/** @file Playback engine — interpolates Scene positions via Konva.Animation. */
import Konva from "konva";
import { appStore } from "../core/state.js";
import { createPlaybackFSM } from "./fsm.js";
import { lerpPoint } from "./lerp.js";
import { metersToPx } from "../core/coords.js";
import { projectSceneToTokens } from "../core/play.js";

/** @typedef {import("../core/play.js").Scene} Scene */
/** @typedef {import("../core/play.js").Token} Token */

/**
 * @param {object} opts
 * @param {Konva.Layer} opts.tokensLayer
 * @param {Konva.Layer} [opts.drawingsLayer]
 * @param {Konva.Animation} [opts._animationOverride]  For tests: inject a mock Animation
 */
export function createPlaybackEngine(opts) {
  const fsm = createPlaybackFSM();
  const { tokensLayer } = opts;

  let currentSceneIndex = 0;
  let elapsedMs = 0;
  let speedMultiplier = 1;
  // dev-only FPS accumulator
  let fpsAccum = 0;
  let fpsFrames = 0;
  let fpsLastReport = 0;

  /**
   * @param {{ time: number, timeDiff: number } | null} frame
   */
  function onFrame(frame) {
    if (!frame) {
      return;
    }
    elapsedMs += frame.timeDiff * speedMultiplier;

    // dev-only FPS meter
    if (typeof window !== "undefined" && frame.timeDiff > 0) {
      fpsAccum += 1000 / frame.timeDiff;
      fpsFrames += 1;
      if (frame.time - fpsLastReport > 500) {
        const avg = fpsAccum / Math.max(1, fpsFrames);
        fpsLastReport = frame.time;
        fpsAccum = 0;
        fpsFrames = 0;
        if (/** @type {any} */ (window).__hbFpsOverlay) {
          /** @type {any} */ (window).__hbFpsOverlay(avg);
        }
      }
    }

    const state = appStore.get();
    const scenes = state.scenes;
    const current = scenes[currentSceneIndex];
    const next = scenes[currentSceneIndex + 1];
    if (!current) {
      stopInternal();
      fsm.dispatch("reset");
      return;
    }
    if (!next) {
      // last scene — snap then stop
      applyScene(currentSceneIndex);
      stopInternal();
      fsm.dispatch("reset");
      return;
    }

    const duration = Math.max(100, current.durationMs);
    const t = elapsedMs / duration;
    if (t >= 1) {
      // Advance to next scene, reset elapsed
      currentSceneIndex += 1;
      elapsedMs = 0;
      applyScene(currentSceneIndex);
      return;
    }

    interpolateFrame(current, next, t);
  }

  /**
   * @param {Scene} from
   * @param {Scene} to
   * @param {number} t
   */
  function interpolateFrame(from, to, t) {
    const state = appStore.get();
    for (const tok of state.tokens) {
      const node = findTokenNode(tok.id);
      if (!node) {
        continue;
      }
      if (tok.kind === "ball") {
        if (from.ball && to.ball) {
          const p = lerpPoint(from.ball, to.ball, t);
          setNodeMeters(node, p);
        } else if (from.ball) {
          setNodeMeters(node, from.ball);
        }
        continue;
      }
      const a = from.tokens[tok.id];
      const b = to.tokens[tok.id];
      if (!a || !b) {
        continue;
      }
      const p = lerpPoint(a, b, t);
      setNodeMeters(node, p);
    }
  }

  /**
   * @param {Konva.Node} node
   * @param {{x:number,y:number}} meters
   */
  function setNodeMeters(node, meters) {
    const t = appStore.get().transform;
    if (!t) {
      return;
    }
    const { px, py } = metersToPx(t, meters);
    node.position({ x: px, y: py });
  }

  /** @param {string} id */
  function findTokenNode(id) {
    return tokensLayer.findOne((/** @type {Konva.Node} */ n) => n.id() === id) ?? null;
  }

  /**
   * Apply a scene instantly (hard cut). Updates activeSceneIndex in state and
   * projects scene positions onto tokens.
   * @param {number} idx
   */
  function applyScene(idx) {
    const state = appStore.get();
    const scene = state.scenes[idx];
    if (!scene) {
      return;
    }
    appStore.set((s) => ({
      activeSceneIndex: idx,
      tokens: projectSceneToTokens(scene, s.tokens),
    }));
  }

  const anim =
    opts._animationOverride ??
    new Konva.Animation((frame) => onFrame(frame ?? null), tokensLayer);

  function play() {
    const state = appStore.get();
    if (state.scenes.length < 2) {
      // no interpolation possible, refuse
      return;
    }
    // resuming from paused? keep currentSceneIndex + elapsedMs
    // starting fresh from idle? start at activeSceneIndex
    if (fsm.state === "idle") {
      currentSceneIndex = state.activeSceneIndex;
      elapsedMs = 0;
    }
    if (!fsm.dispatch("play")) {
      return;
    }
    // disable hit-testing during playback (perf)
    tokensLayer.listening(false);
    anim.start();
  }

  function pause() {
    if (!fsm.dispatch("pause")) {
      return;
    }
    anim.stop();
    tokensLayer.listening(true);
  }

  function reset() {
    stopInternal();
    if (!fsm.dispatch("reset") && fsm.state !== "idle") {
      return;
    }
    currentSceneIndex = 0;
    elapsedMs = 0;
    applyScene(0);
  }

  function stopInternal() {
    anim.stop();
    tokensLayer.listening(true);
  }

  function stepForward() {
    if (fsm.state === "playing") {
      return;
    }
    const state = appStore.get();
    const next = Math.min(state.scenes.length - 1, currentSceneIndex + 1);
    currentSceneIndex = next;
    elapsedMs = 0;
    applyScene(next);
  }

  function stepBackward() {
    if (fsm.state === "playing") {
      return;
    }
    const prev = Math.max(0, currentSceneIndex - 1);
    currentSceneIndex = prev;
    elapsedMs = 0;
    applyScene(prev);
  }

  /** @param {number} idx */
  function jumpToScene(idx) {
    const state = appStore.get();
    const clamped = Math.max(0, Math.min(state.scenes.length - 1, idx));
    currentSceneIndex = clamped;
    elapsedMs = 0;
    applyScene(clamped);
  }

  function getState() {
    return {
      fsmState: fsm.state,
      currentSceneIndex,
      elapsedMs,
      speedMultiplier,
    };
  }

  /**
   * Set a speed multiplier applied to elapsedMs per frame.
   * Invalid values (≤0 or non-finite) are clamped to 1.
   * @param {number} f
   */
  function setSpeedMultiplier(f) {
    speedMultiplier = Number.isFinite(f) && f > 0 ? f : 1;
  }

  /** Testing hook — step one simulated frame without real rAF. */
  function __testTick(timeDiff = 16) {
    onFrame(/** @type {any} */ ({ time: elapsedMs + timeDiff, timeDiff }));
  }

  return {
    play,
    pause,
    reset,
    stepForward,
    stepBackward,
    jumpToScene,
    getState,
    setSpeedMultiplier,
    /** Expose FSM subscription for UI. */
    subscribe: fsm.subscribe,
    __testTick,
  };
}
